import React, { useEffect, useState } from "react";
import { Typography, Radio, Textarea } from "@material-tailwind/react";

interface PreMeetingLawyerEvaluationFormProps {
  onChange: (data: any) => void;
}

type YesNoNa = "Sim" | "Não" | "Não se aplica" | null;

const toBooleanOrNull = (value: YesNoNa): boolean | null => {
  if (value === "Sim") return true;
  if (value === "Não") return false;
  return null; // "Não se aplica"
};

const PreMeetingLawyerEvaluationForm: React.FC<PreMeetingLawyerEvaluationFormProps> = ({ onChange }) => {
  const [formData, setFormData] = useState({
    arrived30To60MinutesEarly: null as YesNoNa,
    confidentAboutCaseInfo: null as YesNoNa,
    overallRating: "", // 1-10
    otherRelevantInfo: "",
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isValid, setIsValid] = useState(false);

  const requiredFields = [
    "arrived30To60MinutesEarly",
    "confidentAboutCaseInfo",
    "overallRating",
  ];

  const ratingBelowSeven =
    formData.overallRating !== "" && Number(formData.overallRating) < 7;

  const validate = () => {
    const ok = requiredFields.every((f) => {
      const value = (formData as any)[f];
      return value !== null && value !== "";
    });
    // Nota abaixo de 7 exige o preenchimento do motivo
    const notesRequired = ratingBelowSeven && formData.otherRelevantInfo.trim() === "";
    setIsValid(ok && !notesRequired);
  };

  useEffect(() => {
    validate();
  }, [formData]);

  useEffect(() => {
    const trimmedNotes = formData.otherRelevantInfo?.trim() || "";
    onChange({
      ...formData,
      isValid,
      formattedData: {
        arrived30To60MinutesEarly: toBooleanOrNull(formData.arrived30To60MinutesEarly),
        confidentAboutCaseInfo: toBooleanOrNull(formData.confidentAboutCaseInfo),
        overallRating: formData.overallRating ? Number(formData.overallRating) : null,
        otherRelevantInfo: trimmedNotes,
        // A API exige ratingJustification para notas abaixo de 7; nesse
        // formulário o motivo é coletado em otherRelevantInfo.
        ...(ratingBelowSeven ? { ratingJustification: trimmedNotes } : {}),
      },
    });
  }, [formData, isValid, ratingBelowSeven, onChange]);

  const markTouched = (name: string) =>
    setTouched((t) => ({ ...t, [name]: true }));

  const isInvalid = (name: string) => {
    const value = (formData as any)[name];
    return touched[name] && (value === null || value === "");
  };

  const yesNoNaField = (name: keyof typeof formData, label: string) => (
    <div>
      <Typography variant="small" className="text-blue-700 font-medium mb-1">
        {label} <span className="text-red-500">*</span>
      </Typography>
      <div className="flex gap-4 flex-wrap">
        {(["Sim", "Não", "Não se aplica"] as const).map((option) => (
          <Radio
            key={option}
            name={name}
            label={option}
            checked={(formData as any)[name] === option}
            onChange={() => {
              markTouched(String(name));
              setFormData((s) => ({ ...s, [name]: option }));
            }}
          />
        ))}
      </div>
      {isInvalid(String(name)) && (
        <Typography className="text-red-500 text-xs mt-1">Este campo é obrigatório</Typography>
      )}
    </div>
  );

  const notesInvalid =
    ratingBelowSeven && touched.otherRelevantInfo && formData.otherRelevantInfo.trim() === "";

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
        <Typography variant="h6" className="text-blue-900 mb-2">
          Avaliação do Preposto - Reunião Prévia
        </Typography>
        <Typography variant="small" className="text-blue-800">
          Por favor, avalie o desempenho do preposto durante a Reunião Prévia.
        </Typography>
      </div>

      {yesNoNaField("arrived30To60MinutesEarly", "O preposto foi pontual?")}
      {yesNoNaField("confidentAboutCaseInfo", "O preposto demonstrou conhecimento do caso?")}

      {/* Avaliação geral (nota 1-10) */}
      <div>
        <Typography variant="small" className="text-blue-700 font-medium mb-1">
          Avaliação geral da preparação do preposto (1 = insuficiente, 10 = excelente) <span className="text-red-500">*</span>
        </Typography>
        <div className="flex gap-2 flex-wrap">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
            <Radio
              key={n}
              name="overallRating"
              label={`${n}`}
              checked={formData.overallRating === String(n)}
              onChange={() => {
                markTouched("overallRating");
                setFormData((s) => ({ ...s, overallRating: String(n) }));
              }}
            />
          ))}
        </div>
        {isInvalid("overallRating") && (
          <Typography className="text-red-500 text-xs mt-1">Este campo é obrigatório</Typography>
        )}
      </div>

      {/* Observações / motivo */}
      <div>
        <Typography variant="small" className="text-blue-700 font-medium mb-1">
          {ratingBelowSeven ? (
            <>
              Descreva o motivo da nota abaixo de 7 <span className="text-red-500">*</span>
            </>
          ) : (
            "Observações adicionais (opcional)"
          )}
        </Typography>
        <Textarea
          name="otherRelevantInfo"
          value={formData.otherRelevantInfo}
          onChange={(e) => {
            markTouched("otherRelevantInfo");
            setFormData((s) => ({ ...s, otherRelevantInfo: e.target.value }));
          }}
          className={`w-full ${notesInvalid ? "border-red-500" : ""}`}
          placeholder={
            ratingBelowSeven
              ? "Explique os motivos da nota atribuída"
              : "Descreva informações adicionais sobre o desempenho do preposto, se necessário"
          }
        />
        {notesInvalid && (
          <Typography className="text-red-500 text-xs mt-1">
            Este campo é obrigatório para notas abaixo de 7
          </Typography>
        )}
      </div>
    </div>
  );
};

export default PreMeetingLawyerEvaluationForm;
